import React from "react";
import { Form, ColorPicker, Row, Col, Card, Button, Space } from "antd";
import { useIntl } from "react-intl";
import { IBrand, ISurveyTheme } from "../../services/nestjs-base-api/types";
import SurveyPreview from "./SurveyPreview";

interface ThemeStepProps {
    formData: {
        name: string;
        brandId: number;
        theme: ISurveyTheme;
    };
    brands: IBrand[];
    errors: Record<string, string>;
    onChange: (updates: any) => void;
}

const ThemeStep: React.FC<ThemeStepProps> = ({
    formData,
    brands,
    errors,
    onChange
}) => {
    const intl = useIntl();

    const selectedBrand = brands.find(b => b.id === formData.brandId);

    const handleColorChange = (colorKey: keyof ISurveyTheme) => (color: any) => {
        const hexColor = color.toHexString();
        onChange({
            theme: {
                ...formData.theme,
                [colorKey]: hexColor
            }
        });
    };

    const applyPreset = (preset: ISurveyTheme) => {
        onChange({ theme: preset });
    };

    const presets = [
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.professional' }),
            theme: {
                primaryColor: selectedBrand?.primaryColor || '#4338ca',
                secondaryColor: '#1e40af',
                backgroundColor: '#1e293b',
                cardColor: '#334155',
                accentColor: '#3b82f6'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.vibrant' }),
            theme: {
                primaryColor: '#059669',
                secondaryColor: '#10b981',
                backgroundColor: '#064e3b',
                cardColor: '#065f46',
                accentColor: '#34d399'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.minimal' }),
            theme: {
                primaryColor: '#374151',
                secondaryColor: '#6b7280',
                backgroundColor: '#111827',
                cardColor: '#1f2937',
                accentColor: '#9ca3af'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.ocean' }),
            theme: {
                primaryColor: '#0891b2',
                secondaryColor: '#06b6d4',
                backgroundColor: '#164e63',
                cardColor: '#155e75',
                accentColor: '#22d3ee'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.sunset' }),
            theme: {
                primaryColor: '#ea580c',
                secondaryColor: '#f97316',
                backgroundColor: '#431407',
                cardColor: '#7c2d12',
                accentColor: '#fb923c'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.royal' }),
            theme: {
                primaryColor: '#7c3aed',
                secondaryColor: '#8b5cf6',
                backgroundColor: '#2e1065',
                cardColor: '#4c1d95',
                accentColor: '#a78bfa'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.rose' }),
            theme: {
                primaryColor: '#e11d48',
                secondaryColor: '#f43f5e',
                backgroundColor: '#4c0519',
                cardColor: '#881337',
                accentColor: '#fb7185'
            }
        },
        {
            name: intl.formatMessage({ id: 'survey.theme.preset.midnight' }),
            theme: {
                primaryColor: '#6366f1',
                secondaryColor: '#818cf8',
                backgroundColor: '#0f0f23',
                cardColor: '#1e1e3f',
                accentColor: '#a5b4fc'
            }
        }
    ];

    return (
        <div>
            <h3>{intl.formatMessage({ id: 'survey.create.step3.title' })}</h3>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
                {intl.formatMessage({ id: 'survey.create.step3.subtitle' })}
            </p>

            <Row gutter={24}>
                <Col xs={24} lg={12}>
                    <Card title={intl.formatMessage({ id: 'survey.theme.customization' })} style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={intl.formatMessage({ id: 'survey.theme.primaryColor' })}>
                                <ColorPicker
                                    value={formData.theme.primaryColor}
                                    onChange={handleColorChange('primaryColor')}
                                    showText
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item label={intl.formatMessage({ id: 'survey.theme.secondaryColor' })}>
                                <ColorPicker
                                    value={formData.theme.secondaryColor}
                                    onChange={handleColorChange('secondaryColor')}
                                    showText
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item label={intl.formatMessage({ id: 'survey.theme.backgroundColor' })}>
                                <ColorPicker
                                    value={formData.theme.backgroundColor}
                                    onChange={handleColorChange('backgroundColor')}
                                    showText
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item label={intl.formatMessage({ id: 'survey.theme.cardColor' })}>
                                <ColorPicker
                                    value={formData.theme.cardColor}
                                    onChange={handleColorChange('cardColor')}
                                    showText
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item label={intl.formatMessage({ id: 'survey.theme.accentColor' })}>
                                <ColorPicker
                                    value={formData.theme.accentColor}
                                    onChange={handleColorChange('accentColor')}
                                    showText
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Form>
                    </Card>

                    <Card title={intl.formatMessage({ id: 'survey.theme.presets' })}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {presets.map((preset, index) => (
                                <Button
                                    key={index}
                                    block
                                    onClick={() => applyPreset(preset.theme)}
                                    style={{
                                        height: 'auto',
                                        padding: '12px 16px',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{preset.name}</span>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            {Object.values(preset.theme).map((color, colorIndex) => (
                                                <div
                                                    key={colorIndex}
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: color,
                                                        borderRadius: 2,
                                                        border: '1px solid #e5e7eb'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </Button>
                            ))}
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title={intl.formatMessage({ id: 'survey.settings.preview' })} style={{ position: 'sticky', top: 24 }}>
                        <SurveyPreview
                            surveyName={formData.name}
                            brandName={selectedBrand?.name || ''}
                            theme={formData.theme}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ThemeStep;